import React from 'react';
import {
  ButtonGroup,
  Button,
  Callout,
  Intent,
} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';

import {ExportType, EXPORT_TYPE_TEXT} from './constants';
import CSVExport from './CSVExport';
import XLSXExport from './XLSXExport';
import HTMLTableExport from './HTMLTableExport';
import PNGExport from './PNGExport';

interface ExportOptionsProps {
  csv: boolean;
  xlsx: boolean;
  html_table: boolean;
  png: boolean;
  blockName?: string;
  exportOptions: any;
  selected: ExportType | null;
  setSelected: (type: ExportType) => void;
  onChange: (key: string) => (value: any) => void;
}

interface OptionButtonProps {
  show: boolean;
  type: ExportType;
  active: boolean;
  onClick: (type: ExportType) => void;
}

interface ExportOptionProps {
  blockName?: string;
  selected: ExportType | null;
  data: any;
  onChange: (key: string) => (value: any) => void;
}

const OptionButton = ({
  show,
  type,
  active,
  onClick,
}: OptionButtonProps) => {
  if (!show) {
    return null;
  }

  return (
    <Button
      text={EXPORT_TYPE_TEXT[type] as string}
      active={active}
      intent={active ? Intent.PRIMARY : Intent.NONE}
      onClick={() => onClick(type)}
    />
  );
};

const NoExport = ({blockName}: any) => (
  <Callout
    icon={IconNames.INFO_SIGN}
  >
    Query will be executed but no attachment will be generated.
    You may reference the results in the message contents like this: {'{{ ' + blockName + '.data }}'}
  </Callout>
);

const ExportOption = ({
  blockName,
  selected,
  data,
  onChange,
}: ExportOptionProps) => {
  if (!selected) {
    return null;
  }

  switch (selected) {
    case ExportType.NO_EXPORT:
      return <NoExport blockName={blockName}/>;
    case ExportType.CSV:
      return <CSVExport
        filename={data.filename}
        delimiter={data.delimiter}
        onChange={onChange}
      />;
    case ExportType.XLSX:
      return <XLSXExport
        filename={data.filename}
        onChange={onChange}
      />;
    case ExportType.HTML_TABLE:
      return <HTMLTableExport
        align={data.align}
        border={data.border}
        cellpadding={data.cellpadding}
        cellspacing={data.cellspacing}
        transpose={data.transpose}
        width={data.width}
        onChange={onChange}
      />;
    case ExportType.PNG:
      return <PNGExport
        embed={data.embed}
        filename={data.filename}
        onChange={onChange}
      />;
      default:
        return null;
  }
};

export const Options = ({
  csv,
  xlsx,
  html_table,
  png,
  blockName,
  selected,
  setSelected,
  exportOptions,
  onChange,
}: ExportOptionsProps) => {
  const onSelectedChange = (type: ExportType) => {
    setSelected(type);
    onChange('type')(type);
  };

  return (
    <div className="export-options-popover">
      <div className="title">Export Options</div>
      <ButtonGroup large={true}>
        <OptionButton
          show={true}
          type={ExportType.NO_EXPORT}
          active={selected === ExportType.NO_EXPORT}
          onClick={onSelectedChange}
        />
        <OptionButton
          show={csv}
          type={ExportType.CSV}
          active={selected === ExportType.CSV}
          onClick={onSelectedChange}
        />
        <OptionButton
          show={xlsx}
          type={ExportType.XLSX}
          active={selected === ExportType.XLSX}
          onClick={onSelectedChange}
        />
        <OptionButton
          show={html_table}
          type={ExportType.HTML_TABLE}
          active={selected === ExportType.HTML_TABLE}
          onClick={onSelectedChange}
        />
        <OptionButton
          show={png}
          type={ExportType.PNG}
          active={selected === ExportType.PNG}
          onClick={onSelectedChange}
        />
      </ButtonGroup>
      <div className="selected-option-content">
        <ExportOption
          blockName={blockName}
          selected={selected}
          data={exportOptions || {}}
          onChange={onChange}
        />
      </div>
    </div>
  )
};

export default Options;
